import memoize from 'lodash-es/memoize'
import { User, Account, AccountMeta, Customer, CustomerMeta, Subscription } from '../types'
import { UserService } from '../services/user-service'
import { AccountService } from '../services/account-service'
import { CustomersService } from '../services/customers-service'
import { SubscriptionsService } from '../services/subscriptions-service'
import { waitUntil } from '../utils'

const userSvc = new UserService()
const acctSvc = new AccountService()
const custSvc = new CustomersService()
const subSvc = new SubscriptionsService()

const cachePeriod = 5 * 60 * 1000 // 5 mins

const cachedUser = memoize(() => {
  return userSvc.getUser()
})

const cachedAccount = memoize(() => {
  return Promise.all([acctSvc.getAccount(), acctSvc.getMetadata()])
})

const cachedCustomer = memoize((id: number) => {
  return Promise.all([custSvc.getCustomer(id), custSvc.getMetadata(id)])
})

const cachedSubscription = memoize((customerId: number) => {
  return subSvc.getSubscriptionForCustomer(customerId)
})

setInterval(() => {
  [cachedUser, cachedAccount, cachedCustomer, cachedSubscription].forEach(c => c.cache.clear())
}, cachePeriod)


interface IPendoOpts {
  apiKey: string;
  visitor: {
    id: number
    email: string
    role: 'customer' | 'writer' | 'admin'
    name: string
  };
  parentAccount?: {
    id: number
    declineRate: number
    averageStarRating: number
    monthlyRevenue: number
    preferredWritersCount: number
    activeSubscriptions: number
    pastDue: boolean
    isAgency: boolean
  };
  account?: Object;
}

interface IPendo {
  initialize(opts: IPendoOpts): void;
}

const waitForPendo = waitUntil(() => <IPendo>window['pendo'], 100, 1000)

function validatePendoOpts(opts: any): opts is IPendoOpts {
  if (!opts.apiKey || typeof opts.apiKey !== 'string') {
    return false;
  }

  if (opts.visitor && typeof opts.visitor.id !== 'number') {
    return false;
  }

  return true
}

function initializePendoForCustomerAndWhiteLabel(location: string): void {
  if (process.env.NODE_ENV === 'production') {
    const matches = location.match(/\/dashboard\/(\d+)/)
    const customerId = matches && +matches[1]
    if (customerId) {
      Promise.all([cachedUser(), cachedAccount(), cachedCustomer(customerId), cachedSubscription(customerId)]).then(([u, [a, am], [c, cm], s]) => {
        if (u && !u.adminImpersonating && a && am && c && cm && s) {
          waitForPendo().then(pendo => {
            const opts = pendoOptsForCustomer(u, Object.assign(a, am), Object.assign(c, cm), s)
            if (validatePendoOpts(opts)) {
              pendo.initialize(opts);
            } else {
              console.error('Invalid Pendo options: ', opts);
            }
          }).catch(error => {
            if (error.message === 'Timeout waiting for object') {
              console.log('Wait for Pendo timed out.')
            } else {
              console.log('Pendo could not initialize: ', error.message)
            }
          })
        }
      }).catch(e => console.log(e))
    } else {
      Promise.all([cachedUser(), cachedAccount()]).then(([u, [a, am]]) => {
        if (u && !u.adminImpersonating && a && am) {
          waitForPendo().then(pendo => {
            const opts = pendoOptsForCustomer(u, Object.assign(a, am))
            pendo.initialize(opts)
          }).catch(error => {
            if (error.message === 'Timeout waiting for object') {
              console.log('Wait for Pendo timed out without customer.')
            } else {
              console.log('Pendo could not initialize without customer: ', error.message)
            }
          })
        }
      }).catch(e => console.log(e))
    }
  }
}

function pendoOptsForCustomer(user: User, account: Account & AccountMeta, customer: Customer & CustomerMeta = null, subscription: Subscription = null): IPendoOpts {
  const opts: IPendoOpts = {
    apiKey: '401ce7ab-7d7a-4697-5b49-7a9b6e0f0940',
    visitor: {
      id: user.id,
      email: user.email,
      role: user.role,
      name: user.firstName + ' ' + user.lastName
    },
    parentAccount: {
      id: account.id,
      declineRate: account.declineRate,
      averageStarRating: account.averageStarRating,
      monthlyRevenue: account.monthlyRevenue,
      preferredWritersCount: account.preferredWritersCount,
      activeSubscriptions: account.activeSubscriptions,
      pastDue: account.pastDue,
      isAgency: account.isAgency
    }
  }
  if (customer) {
    opts.account = {
      id: customer.id,
      name: customer.businessName,
      planLevel: subscription.plan.name,
      planPrice: subscription.plan.price / 100,
      planWords: subscription.plan.words,
      creationDate: customer.createdAt,
      monthlyRevenue: customer.monthlyRevenue,
      declineRate: customer.declineRate,
      averageStarRating: customer.averageStarRating,
      postsReceived: customer.postsReceived,
      preferredWritersCount: customer.preferredWritersCount,
      hasOtherInfo: (customer.otherInfo || '').length > 0,
      onSchedule: customer.onSchedule,
      pastDue: customer.pastDue,
      oldPricing: customer.oldPricing
    }
  }
  return opts
}

function pendoOptsForWriter(user: User): IPendoOpts {
  return {
    apiKey: '401ce7ab-7d7a-4697-5b49-7a9b6e0f0940',
    visitor: {
      id: user.id,
      email: user.email,
      role: user.role,
      name: user.firstName + ' ' + user.lastName
    },
    account: {
      id: user.userableId,
      state: user.userable?.state
    }
  }
}

function initializePendoForWriter(location: string): void {
  if (process.env.NODE_ENV === 'production') {
    cachedUser().then(u => {
      if (u) {
        waitForPendo().then(pendo => {
          const opts = pendoOptsForWriter(u)
          pendo.initialize(opts)
        }).catch(_ => console.log('Pendo could not initialize'))
      }
    })
  }
}

export { initializePendoForCustomerAndWhiteLabel, initializePendoForWriter }
